import * as Sentry from '@sentry/react';
import React, { Component, createRef } from 'react';
import { Link, useLocation, withRouter } from 'react-router-dom';

import moment from 'moment';
import PropTypes from 'prop-types';

import GeofenceIcon from '@material-ui/icons/EditLocationOutlined';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';

import logo from '../../../../../images/shieldgps-logo1-gray.svg';
import DeviceColorAllocator from '../../../services/DeviceColorAllocator';
import urlUtils from '../../../services/DeviceMapUrlUtils';
import LocationDataCache from '../../../services/LocationDataCache';
import LocationDataService from '../../../services/LocationDataService';
import PerfTimingService from '../../../services/PerfTimingService';
import { deviceActivityState } from '../../../services/device-utils-v2.ts';
import DeviceInfoWindow from './DeviceInfoWindow';
import createDeviceMarker from './DeviceMarker';
import MapStyle from './MapStyle.json';

import DeviceFilterControls from './DeviceFilterControls.jsx';
import DeviceSummaryPanel from './DeviceSummaryPanel.jsx';

import LocationHistoryButtons from './LocationHistoryButtons.jsx';
import LocationHistoryPlaybackControls from './LocationHistoryPlaybackControls.jsx';

import LoadingModal from '../LoadingModal.jsx';

import MapButtonPanel from './MapButtonPanel.jsx';

import UserAlertsPanel from './UserAlertsPanel.jsx';

import CurrentLocationButton from './CurrentLocationButton.jsx';

import GeofenceEditorOverlay from './GeofenceEditorOverlay.jsx';
import GeofenceManager from './GeofenceManager';

import DeviceMapModalV2 from './DeviceMapModalV2';

import { LocationHistorySearchPanel } from './LocationHistorySearchPanel.tsx';


import {
  MAP_MARKER_ICON_UPDATED_EVENT,
  defaultMapMarkerIconForModel,
} from '../../../services/DeviceMarkerIcons';
import { getOtherIcon } from '../../../services/OtherMapIcons';

function GeofenceEditorToggleButton({ selected }) {
  const location = useLocation();

  if (selected) {
    const url = urlUtils.buildUrl(location, { mode: 'live', recenterMap: false });
    return (
      <Link id="mapGeofenceBtn" className="map-btn map-btn--selected" to={url} replace>
        <GeofenceIcon />
      </Link>
    );
  }

  const url = urlUtils.buildUrl(location, { mode: 'geofence_editor' });
  return (
    <Link id="mapGeofenceBtn" className="map-btn" to={url} replace>
      <GeofenceIcon />
    </Link>
  );
}

const GOOGLE_API_KEY = 'AIzaSyAwtw2YNsVi33X6snyqPt5dbA5qCj1DAjs';

const LIVE_MODE_REFRESH_DELAY = 10000;

const IMPORTANT_EVENT_TYPES = new Set([
  'trip_started',
  'trip_ended',
  'speeding',
  'harsh_driving',
]);

/**
 * Basically the whole app. 
 * 
 */
class DevicesMap extends Component {
  static Mode = Object.freeze({
    LIVE_MODE: 'live',
    LOCATION_HISTORY_MODE: 'history',
    GEOFENCE_EDITOR_MODE: 'geofence_editor',
  });

  currentMode = null;

  // The time for which locations are rendered on the map
  displayTime = null;

  // Array of device data for the devices this map is displaying (defined via
  // this.props.deviceIds)
  devices = [];

  geofences = [];

  // Map of deviceID => DeviceMarker
  deviceMarkers = {};

  locHistoryMarkers = {};

  visibleDeviceIds = [];

  // Map of deviceId => Polyline
  devicePaths = {};

  liveModeTimer = null;

  state = {
    isLoadingModal: false,

    // The point in time the map is currently displaying
    displayTime: null,

    selectedDeviceId: null,

    // If we're in LOCATION_HISTORY_MODE the period is defined by these parameters
    locHistoryStartTime: null,
    locHistoryEndTime: null,

    // Location History
    showLocationHistoryActionSheet: false,

    // Geofencing
    showGeofenceEditor: false,
    activeGeofence: null,

    // Alerts
    triggerNoActiveDevicesAlert: false,
    triggerMissingLocationDataAlert: false,

    showLocationHistorySearchParams: false,
  }

  googleMapRef = createRef();

  googleMap = null;

  // Used to retrieve device and location data
  locationDataCache = null;

  deviceColorAllocator = null;

  deviceInfoWindow = null;

  perfTimingService = new PerfTimingService();

  tripEventInfoWindow = null;

  onGoogleMapsScriptLoad = () => {
    this.googleMap = this.createGoogleMap();

    this.deviceInfoWindow = new DeviceInfoWindow('window-1',
      document.getElementById('device-info-window'));

    // Allow user to click map to close an info window (if open)
    this.googleMap.addListener('click', () => {
      this.onSetSelectedDevice(null);

      if (this.tripEventInfoWindow) {
        this.tripEventInfoWindow.close();
      }
    });

    this.geofenceManager = new GeofenceManager(this.googleMap);
    this.geofenceManager.onActiveGeofenceChange = (activeGeofence) => {
      this.setState({ activeGeofence });
    };
    this.geofenceManager.reloadGeofences();

    this.syncMapToUriState();
  }

  setupGoogleMaps = () => {
    // https://cuneyt.aliustaoglu.biz/en/using-google-maps-in-react-without-custom-libraries/
    if (!window.google) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
      const before = document.getElementsByTagName('script')[0];
      before.parentNode.insertBefore(script, before);
      script.addEventListener('load', () => {
        this.onGoogleMapsScriptLoad();
      });
    } else {
      this.onGoogleMapsScriptLoad();
    }
  }

  componentDidMount() {
    window.lservice = new LocationDataService(API_BASE_URL); // Expose for testing
    this.locationDataCache = new LocationDataCache({
      locationDataService: window.lservice,
    });
    window.lcache = this.locationDataCache; // Expose for testing

    window.perfTimingService = this.perfTimingService;

    this.deviceColorAllocator = new DeviceColorAllocator();

    this.setupGoogleMaps();

    window.addEventListener(MAP_MARKER_ICON_UPDATED_EVENT, this.onMapMarkerIconUpdated);

    this.setState({ triggerNoActiveDevicesAlert: this.props.deviceIds.length === 0 });
  }

  componentWillUnmount() {
    this.exitLiveMode();
    window.removeEventListener(MAP_MARKER_ICON_UPDATED_EVENT, this.onMapMarkerIconUpdated);
  }

  /**
   * Applies a saved icon selection immediately to live state, cached history
   * blocks, and any marker already rendered on the map.
   */
  onMapMarkerIconUpdated = (event) => {
    const { deviceId, mapMarkerIcon } = event.detail || {};
    this.locationDataCache.updateMapMarkerIcon(deviceId, mapMarkerIcon);

    const device = this.devices.find(item => item.id.toString() === deviceId?.toString());
    if (device) {
      device.mapMarkerIcon = mapMarkerIcon;
    }

    const marker = this.deviceMarkers[deviceId];
    if (marker) {
      marker.update({
        mapMarkerIcon,
        position: device?.position || {},
        status: device ? deviceActivityState(device) : 'inactive',
        isLocationLocked: device?.isLocationLocked || false,
      });
    }
  }

  syncMapToUriState = () => {
    const newParams = urlUtils.parseQueryParams(this.props.location);
    this.setMapState(newParams);
  }

  paramDiffs = (oldParams, newParams) => {
    const fieldsToCompare = ['mode', 'startAt', 'endAt', 'deviceFilter', 'selectedDevice'];
    if (!oldParams || !newParams) {
      return fieldsToCompare;
    }

    return fieldsToCompare.filter(
      field => JSON.stringify(oldParams[field]) !== JSON.stringify(newParams[field]),
    );
  }

  /**
   * Updates the map state based on the new mapParams.
   * 
   * Typically called during initialization and when the URL state changes. mapParams
   * typically is derived from the URL. 
   */
  setMapState = (mapParams) => {
    const paramDiffs = this.paramDiffs(this.currentParams, mapParams);

    this.currentMode = mapParams.mode;
    this.currentParams = { ...mapParams };

    this.syncSelectedDeviceStateToMap(mapParams.selectedDevice);

    if (paramDiffs.length === 0) {
      // Dont do any work if nothing material has changed
      return;
    }

    const visibleDeviceIds = this.getVisibleDeviceIds();
    this.setVisibileDevices(visibleDeviceIds);

    if (paramDiffs.length === 1 && (paramDiffs[0] === 'deviceFilter')) {
      // Dont bother with full refresh if only the deviceFilter list changed
      return;
    }

    // If the mode hasn't changed AND if the history start/end dates haven't changed, then nothing to do
    if (!paramDiffs.includes('mode') && !paramDiffs.includes('startAt') && !paramDiffs.includes('endAt')) {
      // Dont bother with full refresh if only the deviceFilter list changed
      return;
    }

    if (mapParams.mode === 'history' && mapParams.startAt && mapParams.endAt) {
      const startAt = moment(mapParams.startAt);
      const endAt = moment(mapParams.endAt);

      this.exitLiveMode();
      this.exitGeofenceEditorMode();
      this.enterLocHistoryMode(startAt, endAt);
    } else if (mapParams.mode === 'geofence_editor') {
      this.exitLiveMode();
      this.exitLocHistoryMode();
      this.enterGeofenceEditorMode();
    } else {
      // Assumes default mode of 'live'
      this.exitLocHistoryMode();
      this.exitGeofenceEditorMode();
      this.enterLiveMode(mapParams.recenterMap && !mapParams.selectedDevice).then(() => {
        // Have to select device after markers are created/set on the map
        // TODO: 4Apr24 - the timer is a nasty hack for now
        if (mapParams.selectedDevice) {
          setTimeout(() => {
            this.syncSelectedDeviceStateToMap(mapParams.selectedDevice);
          }, 100);
        }
      });
    }
  }

  enterGeofenceEditorMode = () => {
    this.geofenceManager.enterEditMode();
    this.setState({
      showGeofenceEditor: true,
    });
  }

  exitGeofenceEditorMode = () => {
    this.geofenceManager.exitEditMode();
    this.setState({
      showGeofenceEditor: false,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.search !== prevProps.location.search) {
      this.syncMapToUriState();
    }
  }

  /*
   * Places the map into 'Live' mode where the current time is set to now
   * and a timer is set to refresh device data and state every
   * LIVE_MODE_REFRESH_DELAY ms.
   */
  enterLiveMode = (recenterMap) => {
    if (this.liveModeTimer) {
      // Make sure we dont have multiple timers running
      return Promise.resolve();
    }

    this.setState({ isLoadingModal: true });

    this.liveModeTimer = setInterval(() => {
      this.setDisplayTime('live').then(() => this.ensureSelectedDeviceInViewport());
    }, LIVE_MODE_REFRESH_DELAY);

    return this.setDisplayTime('live').then(() => {
      this.setState({ isLoadingModal: false });
      if (recenterMap) {
        this.recenterMap();
      }
    });
  }

  exitLiveMode = () => {
    clearInterval(this.liveModeTimer);
    this.liveModeTimer = null;
  }

  /**
   * Updates the current time of the map and then triggers a refresh of
   * device and location data for that time.
   *
   * Returns a Promise that is resolved once all data loaded and state
   * refreshed.
   */
  setDisplayTime = (t) => {
    const displayTime = t === 'live' ? moment() : t;
    this.displayTime = displayTime;
    this.setState({
      displayTime,
    });

    return this.refreshDevices(t === 'live');
  }

  ensureSelectedDeviceInViewport = () => {
    const selectedDevice = this.selectedDevice();

    if (!selectedDevice) {
      return;
    }

    const { latitude, longitude } = selectedDevice.position;
    const position = new window.google.maps.LatLng(
      latitude,
      longitude,
    );

    if (!this.googleMap.getBounds().contains(position)) {
      this.googleMap.panTo(position);
    }
  }

  /**
   * Reloads device & location data for each props.deviceIds at
   * t=this.displayTime
   */
  refreshDevices = (isLive) => {
    const { deviceIds } = this.props;
    if (deviceIds.length === 0) {
      return Promise.resolve();
    }

    if (isLive) {
      return this.locationDataCache.getLatestLocations(deviceIds).then((devices) => {
        this.devices = devices.map(device => ({
          id: device.id,
          imei: device.imei,
          name: device.name,
          model: device.model,
          mapMarkerIcon: device.mapMarkerIcon,
          audioEnabled: (device.audio && device.audio.enabled) || false,
          phoneNumber: (device.audio && device.audio.phoneNumberFormatted),
          position: device.lastPosition,
          lastseenAt: device.lastseenAt,
          isLocationLocked: device.isLocationLocked,
          battery: device.battery,
          batteryMode: device.batteryMode,
        }));
        this.refreshDeviceMarkers();
        this.setState({
          devicesRefreshAt: moment(),
          triggerMissingLocationDataAlert: this.devices.some(device => !device.position),
        });
      });
    }

    const timer = this.perfTimingService.newTimer('refreshDevices');

    // TODO: Optimize by querying for all devices in single call
    const promises = deviceIds.map(deviceId => this.locationDataCache.getLocationAtOrBefore(
      deviceId, this.displayTime,
    ));

    // About 15ms is spent in each block.getLocationAtOrBefore..
    return Promise.all(promises)
      .then((devices) => {
        timer.stop();
        this.devices = devices;
        this.refreshDeviceMarkers();
        this.setState({ mapUpdatedAt: moment() });
      });
  }

  toggleMapType = () => {
    if (this.googleMap.getMapTypeId() === 'satellite') {
      this.googleMap.setMapTypeId('roadmap');
    } else {
      this.googleMap.setMapTypeId('satellite');
    }
  }

  createGoogleMap = () => new window.google.maps.Map(
    this.googleMapRef.current,
    {
      minZoom: 3,
      zoom: 4,
      center: {
        lat: 39.5,
        lng: -98.4,
      },
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DEFAULT,
      },
      clickableIcons: false,
      styles: MapStyle,
    },
  );

  // Update markers to refresh latest state of this.devices
  // New markers created for device if don't already exist
  refreshDeviceMarkers = () => {
    this.devices.forEach((device) => {
      if (!device.position) {
        return;
      }

      const deviceMarker = this.findOrCreateDeviceMarker(device);
      deviceMarker.update({
        visible: this.isDeviceVisible(device.id),
        position: device.position,
        status: deviceActivityState(device),
        isLocationLocked: device.isLocationLocked,
        mapMarkerIcon: device.mapMarkerIcon || defaultMapMarkerIconForModel(device.model),
      });
    });
  }

  isDeviceVisible = deviceId => this.visibleDeviceIds.includes(deviceId.toString());

  refreshDevicePaths = () => {
    Object.entries(this.devicePaths).forEach(([deviceId, path]) => {
      const isVisible = this.isDeviceVisible(deviceId);
      path.setMap(isVisible ? this.googleMap : null);
    });
  }

  refreshLocHistoryMarkers = () => {
    Object.entries(this.locHistoryMarkers).forEach(([deviceId, markers]) => {
      const isVisible = this.isDeviceVisible(deviceId);
      markers.forEach((marker) => {
        marker.setMap(isVisible ? this.googleMap : null);
      });
    });
  }

  findOrCreateDeviceMarker = (device) => {
    let marker = this.deviceMarkers[device.id];
    if (!marker) {
      marker = this.createMarker(device);
      this.deviceMarkers[device.id] = marker;
    }

    return marker;
  }

  createMarker = (device) => {
    const latLng = new window.google.maps.LatLng(
      device.position.latitude,
      device.position.longitude,
    );

    const marker = createDeviceMarker({
      position: latLng,
      title: device.name,
      map: this.googleMap,
      mapMarkerIcon: device.mapMarkerIcon || defaultMapMarkerIconForModel(device.model),
      heading: device.position.heading,
      status: deviceActivityState(device),
      isLocationLocked: device.isLocationLocked,
    });

    marker.addListener('click', () => {
      this.onSetSelectedDevice(device.id);
    });

    return marker;
  }

  syncSelectedDeviceStateToMap = (deviceId) => {
    // Find marker corresponding to deviceId and set it as selected
    if (!deviceId) {
      this.setState({
        selectedDeviceId: null,
      });

      // make sure no marker is selected
      Object.values(this.deviceMarkers).forEach(m => m.setSelected(false));
      this.deviceInfoWindow.close();
    }

    const marker = this.deviceMarkers[deviceId];
    if (!marker) {
      // Invalid deviceId or markers haven't been initialized yet
      return;
    }

    Object.values(this.deviceMarkers).forEach(m => m.setSelected(false));
    marker.setSelected(true);

    this.deviceInfoWindow.open(marker);
    this.googleMap.panTo(marker.position);

    this.setState({
      selectedDeviceId: parseInt(deviceId),
    });
  };


  recenterMap = () => {
    const markers = Object.values(this.deviceMarkers);
    if (markers.length === 0) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    markers.forEach((marker) => {
      bounds.extend(marker.position);
    });

    this.googleMap.fitBounds(bounds);
    if (this.googleMap.getZoom() > 14) {
      this.googleMap.setZoom(14);
    }
  }

  /*
   * Enters and exits from the LocationHistoryMode.
   */
  onLocHistoryOptionChange = (option) => {
    if (!option) {
      // History mode -> live mode
      const url = urlUtils.buildUrl(this.props.location,
        {
          mode: 'live',
          recenterMap: true,
          startAt: null,
          endAt: null,
        });
      this.props.history.replace(url);
      return;
    }

    // There's a special option to show the custom search form
    if (option.value === 0) {
      this.setState({ showLocationHistorySearchParams: true });
      return;
    }

    const url = urlUtils.buildUrl(
      this.props.location,
      {
        mode: 'history',
        startAt: moment().subtract(option.value, option.unit).toISOString(),
        endAt: moment().toISOString(),
      },
    );
    this.props.history.replace(url);
  }

  resetDevicePaths = () => {
    Object.values(this.devicePaths).forEach((path) => {
      path.setMap(null);
    });
    this.devicePaths = {};
  }

  resetLocHistoryMarkers = () => {
    Object.values(this.locHistoryMarkers).forEach((deviceMarkers) => {
      deviceMarkers.forEach((marker) => {
        if (marker) {
          marker.setMap(null);
        }
      });
    });
    this.locHistoryMarkers = {};

    if (this.tripEventInfoWindow) {
      this.tripEventInfoWindow.close();
    }
  }

  enterLocHistoryMode = (startTime, endTime) => {
    this.setState({ isLoadingModal: true });

    Sentry.addBreadcrumb({
      category: 'interaction',
      message: 'Entering location history mode',
      level: Sentry.Severity.Info,
    });

    this.exitLiveMode();

    const startTimeParsed = moment(startTime);
    const endTimeParsed = moment(endTime);

    this.resetDevicePaths();
    this.resetLocHistoryMarkers();

    return this.loadLocHistory(startTime, endTime)
      // Start at beginning of trip
      .then(() => this.setDisplayTime(startTime))
      .then(() => {
        this.setState({
          showLocationHistoryActionSheet: true,
          locHistoryStartTime: startTimeParsed,
          locHistoryEndTime: endTimeParsed,
          isLoadingModal: false,
        });

        this.fitMapToPaths();
      })
      .catch((e) => {
        console.error('Unable to enter location playback mode');
        console.error(e);

        this.setState({
          isLoadingModal: false,
        });
      });
  }

  exitLocHistoryMode = () => {
    Sentry.addBreadcrumb({
      category: 'interaction',
      message: 'Exiting location history mode',
      level: Sentry.Severity.Info,
    });

    this.resetDevicePaths();
    this.resetLocHistoryMarkers();
    this.setState({
      showLocationHistoryActionSheet: false,
    });
  }

  fitMapToPaths = () => {
    const bounds = new window.google.maps.LatLngBounds();
    let boundPointCounter = 0;

    this.visibleDeviceIds.forEach((deviceId) => {
      const path = this.devicePaths[deviceId];
      for (let i = 0; i < path.getPath().length; i += 1) {
        const point = path.getPath().getAt(i);
        bounds.extend(point);
        boundPointCounter += 1;
      }
    });

    if (boundPointCounter > 0) {
      this.googleMap.fitBounds(bounds);
    }
  }

  loadLocHistory = (startTime, endTime) => {
    // TODO: Optimize by querying for all devices in single call
    const promises = this.props.deviceIds
      .map(deviceId => this.locationDataCache.getLocationsBetween(
        deviceId, startTime, endTime,
      ));

    return Promise.all(promises)
      .then((blocks) => {
        blocks.forEach(block => this.drawLocationHistory(block));
      });
  }

  drawLocationHistory = (block) => {
    try {
      this.drawDevicePath(block);
      this.drawDeviceEvents(block);
    } catch (e) {
      Sentry.captureMessage(`Unable to draw location history for device #${block.device.id}`);
      Sentry.captureException(e);
      console.error(e);
    }
  };

  drawDeviceEvents = (block) => {
    const deviceId = block.device.id;

    const importantEvents = block.events.filter(e => IMPORTANT_EVENT_TYPES.has(e.event_type));

    // Only draw the markers if the device is visible
    const deviceVisible = this.isDeviceVisible(deviceId);

    let markers = importantEvents.map(e => this.renderEventMarker(e, deviceVisible));

    // 11Aug21 - BUGFIX - markers with no position cause client to bomb
    markers = markers.filter(m => m !== null);

    this.locHistoryMarkers[deviceId] = markers;
  }

  renderEventMarker = (event, isVisible) => {
    if (!event || !event.event_data || !event.event_data.lat || !event.event_data.lng) {
      return null;
    }

    const position = new window.google.maps.LatLng(
      event.event_data.lat,
      event.event_data.lng,
    );

    const icon = this.getEventMarkerIcon(event.event_type);
    if (!icon) {
      return null;
    }

    const marker = new window.google.maps.Marker({
      position,
      icon: this.getEventMarkerIcon(event.event_type),
    });
    marker.addListener('click', () => {
      if (!this.tripEventInfoWindow) {
        this.tripEventInfoWindow = new window.google.maps.InfoWindow();
      }

      this.tripEventInfoWindow.setContent(`<strong>${this.getTripEventDescription(event)}</strong>`);
      this.tripEventInfoWindow.open(marker.getMap(), marker);
    });

    if (isVisible) {
      marker.setMap(this.googleMap);
    }

    return marker;
  }

  getTripEventDescription = (event) => {
    const TEMPLATES = {
      trip_started: (e) => {
        let detail = 'Trip Started';

        const address = e.event_data.geocoded_address;
        if (address) {
          detail += `<br><br>${address}`;
        }

        return detail;
      },
      trip_ended: (e) => {
        let detail = 'Trip Ended';

        const address = e.event_data.geocoded_address;
        if (address) {
          detail += `<br><br>${address}`;
        }

        return detail;
      },
      speeding: (e) => {
        let detail = 'Speeding';

        const speedMph = e.event_data.speed_mph;
        const speedLimitMph = e.event_data.speed_limit_mph;
        if (speedMph && speedLimitMph) {
          detail += ` (${speedMph.toFixed(0)} > ${speedLimitMph.toFixed(0)} mph limit)`;
        }

        return detail;
      },
      harsh_driving: (e) => {
        if (e.event_data.report_type === 'HARSH_BRAKING') {
          return 'Harsh Braking';
        }

        if (e.event_data.report_type === 'HARSH_ACCEL') {
          return 'Harsh Acceleration';
        }

        return 'Harsh Driving';
      },
    };

    const descriptionFn = TEMPLATES[event.event_type];
    if (!descriptionFn) {
      return 'Unknown Event';
    }

    return descriptionFn(event);
  }

  getEventMarkerIcon = (eventType) => {
    const MAPPING = {
      trip_started: 'start',
      trip_ended: 'stop',
      speeding: 'warning',
      harsh_driving: 'warning',
    };

    const icon = getOtherIcon(MAPPING[eventType]);
    if (!icon) {
      return null;
    }

    return {
      url: getOtherIcon(MAPPING[eventType]).src,
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: new window.google.maps.Point(15, 15),
    };
  }

  drawDevicePath = (block) => {
    const deviceId = block.device.id;

    const pathPoints = block.positions.map(pos => new window.google.maps.LatLng(
      pos.latitude,
      pos.longitude,
    ));

    const path = new window.google.maps.Polyline({
      path: pathPoints,
      geodesic: true,
      strokeColor: this.deviceColorAllocator.getColor(deviceId),
      strokeOpacity: 1,
      strokeWeight: 3,
    });

    const existingPath = this.devicePaths[deviceId];
    if (existingPath) {
      existingPath.setMap(null);
    }
    this.devicePaths[deviceId] = path;

    // Only draw the path if the device is visible
    const deviceVisible = this.isDeviceVisible(deviceId);
    path.setMap(deviceVisible ? this.googleMap : null);
  }

  /**
   * A list of options to display in the deviceFilterMenu
   */
  deviceFilterMenuOptions = () => this.devices.map(device => ({
    id: device.id,
    name: device.name,
    visible: this.isDeviceVisible(device.id),
  }));

  /**
   * Called to toggle the visibility of devices on the map. 
   * 
   * Constructs a set of new visible deviceIds and then replaces the URL of the page
   * with it set.
   * 
   */
  deviceFilterChange = (deviceId, isVisible) => {
    const s = new Set(this.visibleDeviceIds);
    if (isVisible) {
      s.add(deviceId.toString());
    } else {
      s.delete(deviceId.toString());
    }

    const url = urlUtils.buildUrl(this.props.location, { deviceFilter: Array.from(s) });
    this.props.history.replace(url);
  }

  onSetSelectedDevice = (deviceId) => {
    const url = urlUtils.buildUrl(this.props.location, { selectedDevice: deviceId });
    this.props.history.replace(url);
  }

  getVisibleDeviceIds = () => {
    const { deviceFilter } = urlUtils.parseQueryParams(this.props.location);
    let { deviceIds } = this.props;

    if (deviceFilter) {
      // TODO: Should really be the intersetion of deviceFilter + this.props.deviceIds
      deviceIds = deviceFilter;
    }

    return deviceIds.map(id => id.toString());
  }

  setVisibileDevices = (visibleDeviceIds) => {
    this.visibleDeviceIds = visibleDeviceIds;
    this.refreshDeviceMarkers();
    this.refreshDevicePaths();
    this.refreshLocHistoryMarkers();

    // We dont manage the state of the markers, paths, visible devices through React state
    // since we want immediate (synchronous) access to them. But we do need to force a UI
    // update here since all of the work above has been completed
    this.setState({
      mapUpdatedAt: moment(),
    });
  }

  isDeviceFilterActive = () => {
    const { deviceFilter } = urlUtils.parseQueryParams(this.props.location);
    return deviceFilter && deviceFilter.length !== this.props.deviceIds.length;
  }

  getDevice = deviceId => this.devices.find(
    device => device.id === deviceId,
  );

  selectedDevice = () => {
    if (!this.state.selectedDeviceId) {
      return null;
    }

    return this.getDevice(this.state.selectedDeviceId);
  }

  leaveGeofenceEditorMode = () => {
    const url = urlUtils.buildUrl(this.props.location,
      {
        mode: 'live',
        recenterMap: false,
      });
    this.props.history.replace(url);
  }

  onGeofenceSave = (params) => {
    return this.geofenceManager.saveActiveGeofence(params).then(() => {
      this.leaveGeofenceEditorMode();
    });
  }

  onGeofenceDelete = () => {
    return this.geofenceManager.deleteActiveGeofence().then(() => {
      this.leaveGeofenceEditorMode();
    });
  }

  onLocationHistoryParamsSave = (startTime, endTime) => {
    this.setState({ showLocationHistorySearchParams: false });

    const url = urlUtils.buildUrl(
      this.props.location,
      {
        mode: 'history',
        startAt: startTime.toISOString(),
        endAt: endTime.toISOString(),
      },
    );
    this.props.history.replace(url);

  }

  render() {
    const currentMode = urlUtils.parseQueryParams(this.props.location).mode;

    return (
      <div className="devices-map">
        <LoadingModal open={this.state.isLoadingModal } />

        <div className="map-header">
          <img className="map-header__logo shadow" src={logo} />
        </div>

        <MapButtonPanel position="top-right">
          <a id="mapTypeBtn" className="map-btn"
            onClick={this.toggleMapType}>
              <LayersOutlinedIcon />
            </a>

          <DeviceFilterControls
            selected={this.isDeviceFilterActive()}
            options={this.deviceFilterMenuOptions()}
            onChange={this.deviceFilterChange}
          />

          <LocationHistoryButtons
            selected={currentMode === 'history'}
            onOptionSelect={this.onLocHistoryOptionChange}/>

          <GeofenceEditorToggleButton
            selected={currentMode === 'geofence_editor'}/>
        </MapButtonPanel>

        <MapButtonPanel position="bottom-right">
          <CurrentLocationButton map={this.googleMap} />
        </MapButtonPanel>

        <div id="device-info-window">
          <div className="drag-handle"></div>
          <DeviceSummaryPanel
            device={this.selectedDevice()}
            user={this.props.user}
            onDeviceRefresh={() => this.refreshDevices(true)}
          />
        </div>

        <div
          id="google-map"
          ref={this.googleMapRef}
        />

        { this.state.showLocationHistoryActionSheet ? (
          // No need to have this panel in the DOM
          <LocationHistoryPlaybackControls
            open={this.state.showLocationHistoryActionSheet}
            startTime={this.state.locHistoryStartTime}
            endTime={this.state.locHistoryEndTime}
            onTimeChange={this.setDisplayTime}
            onClose={() => this.onLocHistoryOptionChange(null)} />
        ) : null}

        <GeofenceEditorOverlay
          open={this.state.showGeofenceEditor}
          geofence={this.state.activeGeofence}  // Maybe dont need
          onSave={this.onGeofenceSave}
          onDelete={this.onGeofenceDelete}
          onClose={this.leaveGeofenceEditorMode}
          />

        <UserAlertsPanel
          user={this.props.user}
          triggerNoActiveDevicesAlert={this.state.triggerNoActiveDevicesAlert}
          triggerMissingLocationDataAlert={this.state.triggerMissingLocationDataAlert}
          triggerInRecoveryAlert={this.props.user.inRecovery}
          triggerStripeMigrationAlert={this.props.user.stripeMigrationRequired}
        />

        <DeviceMapModalV2 open={this.state.showLocationHistorySearchParams} onClose={() => this.setState({ showLocationHistorySearchParams: false })} >
          <LocationHistorySearchPanel onSave={this.onLocationHistoryParamsSave} />
        </DeviceMapModalV2>
      </div>
    );
  }
}

DevicesMap.propTypes = {
  user: PropTypes.object.isRequired,
  deviceIds: PropTypes.array.isRequired,
};

export default withRouter(DevicesMap);
